/**
 * 移入批次（工具栏）/ 移动包牌（详情勾选包牌）抽屉，**接入后端**（Q29）：
 * 输入包牌号 → 提交解析（`previewSortingBatchMoveIn`）→ 每个包牌一行选择目标批次（Q8）→ 执行移入（`moveIntoSortingBatch`）→ 结果表。
 *
 * 执行后**不写回页面 mock 状态**：mock 批次 id 与真实批次 id 会撞号，写回会改错别的批次；
 * 列表与详情已接入后端，执行后按操作名刷新当前挂着的列表、详情包牌与操作历史（[SORTING_BATCH_MOVE_REFETCH_QUERIES]）。
 */
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, Drawer, message } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MOVE_INTO_SORTING_BATCH, MoveIntoSortingBatchData, MoveResultRowApi, SORTING_BATCH_MOVE_REFETCH_QUERIES } from '../graphql/mutations/sortingBatchMove';
import {
    MoveInBagItemApi,
    MoveInOrphanApi,
    PREVIEW_SORTING_BATCH_MOVE_IN,
    PreviewSortingBatchMoveInData,
} from '../graphql/queries/previewSortingBatchMove';
import { MoveInOrphan, MoveInResultRow } from '../types';
import { moveErrorMessage } from '../utils/moveErrorMessage';
import { isBagLikeNumber, splitNumbers } from '../utils/numberInputUtils';
import { MoveInBagTable } from './MoveInBagTable';
import { MoveInOrphanList } from './MoveInOrphanList';
import { MoveInResultTable } from './MoveInResultTable';
import { MOVE_DRAWER_WIDTH, MoveNumberInput } from './MoveInShared';
import { MoveResultNotices } from './MoveResultNotices';
import { MoveInDrawerProps } from './overlayProps';

interface Preview {
    items: MoveInBagItemApi[];
    orphans: MoveInOrphanApi[];
}

interface Session {
    text: string;
    /** 提交解析得到的结果；文本修改后清空 */
    preview: Preview | null;
    /** 包牌号 → 所选目标批次 id（默认第一个候选） */
    selections: Record<string, number>;
    /** 执行结果；非空即锁定表单 */
    executed: MoveResultRowApi[] | null;
}

const emptySession = (text = ''): Session => ({ text, preview: null, selections: {}, executed: null });

/** 后端无法移入项 → 「无法移入」列表的展示结构 */
const toOrphan = (o: MoveInOrphanApi): MoveInOrphan => ({
    number: o.number,
    isBag: isBagLikeNumber(o.number),
    destination: '',
    reason: o.reason,
    currentBatch: o.currentBatch?.batchName,
});

export const MoveInBackendDrawer: React.FC<MoveInDrawerProps> = ({ open, source, warehouseId, onClose }) => {
    const { t } = useTranslation();
    const [session, setSession] = useState<Session>(emptySession);
    const [previewQuery, { loading: parsing }] = useLazyQuery<PreviewSortingBatchMoveInData>(PREVIEW_SORTING_BATCH_MOVE_IN, {
        fetchPolicy: 'no-cache',
    });
    const [moveIn, { loading: executing }] = useMutation<MoveIntoSortingBatchData>(MOVE_INTO_SORTING_BATCH, {
        refetchQueries: SORTING_BATCH_MOVE_REFETCH_QUERIES,
    });

    const parse = async (numbers: string[]) => {
        const { data, error } = await previewQuery({ variables: { warehouseId, numbers } });
        const result = data?.previewSortingBatchMoveIn;
        if (error || !result) {
            message.error(moveErrorMessage(error, t));
            return;
        }
        const selections: Record<string, number> = {};
        result.items.forEach((i) => {
            if (i.candidates.length) selections[i.bagNumber] = i.candidates[0].id;
        });
        setSession((s) => ({ ...s, preview: { items: result.items, orphans: result.orphans }, selections, executed: null }));
    };

    // 每次打开（或来源变化）重置；来自详情的「移动包牌」预填号码并立即解析
    const sourceKey = source ? `${source.batchNumber}|${source.numbers.join(',')}` : '';
    useEffect(() => {
        if (!open) return;
        setSession(emptySession(source ? source.numbers.join('\n') : ''));
        if (source && source.numbers.length) parse(source.numbers);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, sourceKey]);

    const { preview, executed } = session;
    const locked = !!executed;
    const movableCount = preview ? preview.items.length : 0;
    const orphanCount = preview ? preview.orphans.length : 0;
    const summary = preview && (movableCount || orphanCount)
        ? t('wms.sortingBatch.moveIn.parsedSummary', { total: movableCount + orphanCount, ok: movableCount, bad: orphanCount }) ?? ''
        : '';

    const changeText = (text: string) => setSession((s) => ({ ...s, text, preview: null, selections: {}, executed: null }));

    const submit = () => {
        const numbers = splitNumbers(session.text);
        if (!numbers.length) {
            message.warning(t('wms.sortingBatch.moveIn.emptyInput'));
            return;
        }
        parse(numbers);
    };

    const select = (bagNumber: string, batchId: number) =>
        setSession((s) => ({ ...s, selections: { ...s.selections, [bagNumber]: batchId } }));

    const targetBatchName = (bagNumber: string | null): string | undefined => {
        const item = preview?.items.find((i) => i.bagNumber === bagNumber);
        const id = bagNumber ? session.selections[bagNumber] : undefined;
        return item?.candidates.find((b) => b.id === id)?.batchName;
    };

    const execute = async () => {
        if (!preview || locked) return;
        const items = preview.items
            .filter((i) => session.selections[i.bagNumber] !== undefined)
            .map((i) => ({ bagNumber: i.bagNumber, targetBatchId: session.selections[i.bagNumber] }));
        if (!items.length) return;
        try {
            const { data } = await moveIn({ variables: { warehouseId, items } });
            setSession((s) => ({ ...s, executed: data?.moveIntoSortingBatch.rows ?? [] }));
        } catch (e) {
            message.error(moveErrorMessage(e, t));
        }
    };

    const resultRows: MoveInResultRow[] = (executed ?? []).map((r) => ({
        number: r.number,
        bag: r.bagNumber ?? r.number,
        batchNumber: r.batchName ?? targetBatchName(r.bagNumber ?? r.number),
        result: r.success ? (r.needReprint ? 'MOVED_IN_REPRINT' : 'MOVED_IN') : 'FAILED',
        reason: r.resultCode ?? undefined,
        failedOrderCount: r.failedOrderCount,
    }));

    const footer = (
        <div className="flex justify-end gap-2.5">
            <Button onClick={onClose}>{t('wms.sortingBatch.moveIn.cancel')}</Button>
            {executed ? (
                // key 区分实例：复用执行按钮时 antd 内部 loading 状态会滞后一帧
                <Button key="done" type="primary" onClick={onClose}>{t('wms.sortingBatch.common.done')}</Button>
            ) : (
                <Button key="execute" type="primary" disabled={!movableCount} loading={executing} onClick={execute}>
                    {t('wms.sortingBatch.moveIn.execute')}
                </Button>
            )}
        </div>
    );

    return (
        <Drawer
            open={open}
            onClose={onClose}
            destroyOnHidden
            width={MOVE_DRAWER_WIDTH}
            title={source ? t('wms.sortingBatch.moveIn.titleMoveBags') : t('wms.sortingBatch.moveIn.titleMoveIn')}
            footer={footer}
        >
            <div className="flex flex-col gap-3.5">
                {source ? (
                    <div className="rounded border border-solid border-[#e9dcff] bg-[#f7f3ff] px-3 py-2 text-[12.5px] text-[#4e5969]">
                        {t('wms.sortingBatch.moveIn.sourceHint', { batch: source.batchNumber, count: source.numbers.length })}
                    </div>
                ) : null}
                <div className={`flex flex-col gap-3.5 ${locked ? 'pointer-events-none opacity-60' : ''}`}>
                    <MoveNumberInput
                        ns="moveIn"
                        value={session.text}
                        summary={summary}
                        disabled={locked || parsing}
                        onChange={changeText}
                        onSubmit={submit}
                        bagOnly
                    />
                    {preview && preview.items.length ? (
                        <MoveInBagTable items={preview.items} selections={session.selections} disabled={locked} onSelect={select} />
                    ) : null}
                </div>
                {preview ? <MoveInOrphanList orphans={preview.orphans.map(toOrphan)} /> : null}
                {executed ? (
                    <>
                        <MoveResultNotices rows={executed} targetBatchOf={(r) => targetBatchName(r.bagNumber ?? r.number)} />
                        <MoveInResultTable rows={resultRows} />
                    </>
                ) : null}
            </div>
        </Drawer>
    );
};
